import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import BetTableDragon6 from "../../casinoComponents/betTableDragon6";
import VideoBox from "../../casinoComponents/videoBox";
import MyBet from "../../casinoComponents/myBet";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import ViewMoreModal from "../../../components/ViewMoreModal";
import Dt6CardsOverlay from "../../casinoComponents/videoBox/overlays/Dt6CardsOverlay";
import ResultDragonTiger6 from "../resultui/resultDragonTiger6/ResultDragonTiger6";
import VDt6View from "./VDt6View";
import "../../../styles/games/dt6.css";
import {
  getCasinoGameDetails,
  getMatchExposure,
  getCasinoOpenBets,
} from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";

// Reference game-header titles per API type (reference: lastresults2 res1.cname)
const TITLES = {
  dt6: "1 Day Dragon Tiger",
  pdt6: "Premium 1 Day Dragon Tiger",
  tdt6: "Tembo 1 Day Dragon Tiger",
  vdt6: "V-1 Day Dragon Tiger",
};

/**
 * 1 Day Dragon Tiger — casino/dt6, casino/pdt6, casino/tdt6 (reference .casino-table.dt1day)
 * and vcasino/dt6 (API type vdt6, reference .casino-table.vcasino.vdt1day → VDt6View).
 */
export default function DragonTiger6() {
  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "dt6";

  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const feed = gameData?.data?.data ?? gameData?.data ?? {};
  const subData = feed?.sub || [];
  const matchId = feed?.mid?.toString() || gameData?.mid?.toString();

  // Game data: poll every 1.5s
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };
    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [gameName]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            exposureMap[item.team_name] = parseFloat(item.exposure_amount) || 0;
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  // My Bets: getCasinoOpenBets(gameName), poll every 5s
  const fetchMyBetsData = useCallback(async () => {
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype:
            bet.gametype ??
            bet.game_type ??
            (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [gameName]);

  useEffect(() => {
    if (!matchId) return;
    const t = setTimeout(fetchExposure, 2000);
    const exposureInterval = setInterval(fetchExposure, 2000);
    return () => {
      clearTimeout(t);
      clearInterval(exposureInterval);
    };
  }, [matchId, fetchExposure]);

  useEffect(() => {
    const first = setTimeout(fetchMyBetsData, 0);
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => {
      clearTimeout(first);
      clearInterval(intervalId);
    };
  }, [fetchMyBetsData]);

  // Admin panel places no bets: an odds click just refreshes exposure + My Bets
  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  const viewMoreModal = (
    <ViewMoreModal
      show={showViewMoreModal}
      onHide={() => setShowViewMoreModal(false)}
      title="View More"
      sectionKey={gameName}
    />
  );

  if (gameName === "vdt6") {
    return (
      <VDt6View
        gameName={gameName}
        title={TITLES.vdt6}
        data={feed}
        exposures={exposures}
        onBetClick={handleBetClick}
        myBets={myBets}
        onViewMore={() => setShowViewMoreModal(true)}
        viewMoreModal={viewMoreModal}
      />
    );
  }

  const resultData = gameData?.lrs ?? feed?.lrs ?? [];
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const roundId = feed?.mid || gameData?.mid;
  const title = TITLES[gameName] || TITLES.dt6;

  return (
    <CasinoTableLayout
      tableClass="dt1day"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading
          name={title}
          roundId={roundId || ""}
          placedBetCount={myBets.length}
          activeTab="game"
          onTabChange={() => {}}
        />

        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={feed?.lt}
          gameType={gameName}
          cardsOverlay={feed?.card != null ? <Dt6CardsOverlay card={feed.card} /> : null}
        />

        {/* Reference renders .casino-detail once the feed has a round (casinoData.mid) */}
        {roundId ? (
          <div className="casino-detail">
            <BetTableDragon6
              data={subData}
              onBetClick={handleBetClick}
              exposures={exposures}
              remark={feed?.remark}
            />{" "}
            <CasinoLastResult
              gameType={gameName}
              data={resultData}
              gameData={gameData}
              viewAllLink={`/admin/reports/casinoresult/${gameName}`}
              resultComponent={<ResultDragonTiger6 gameType={gameName} data={resultData} />}
            />
          </div>
        ) : null}
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}
